import React, { useState } from 'react';
import { Heading, Text } from '../../../components/Typography';
import TextInput from '../../../components/UI/TextInput';
import Button from '../../../components/UI/Button';
import styles from './SearchTestPage.module.scss';
import { testSearch, SearchBackend, type SearchFilters } from '../../../api/adminSearchApi';
import ResultCard, { type SearchResult } from './components/ResultCard';

const SearchTestPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [topK, setTopK] = useState('10');
  const [backend, setBackend] = useState<SearchBackend>(SearchBackend.TURBOPUFFER);
  const [namespace, setNamespace] = useState('docs-staging');

  // New filter inputs
  const [companyId, setCompanyId] = useState('');
  const [fiscalYear, setFiscalYear] = useState('');
  const [fiscalQuarter, setFiscalQuarter] = useState('');
  const [calendarYear, setCalendarYear] = useState('');
  const [calendarQuarter, setCalendarQuarter] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');

  // State for results and loading/error
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTime, setSearchTime] = useState<number | null>(null);

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);
    setResults([]);
    setSearchTime(null);

    try {
      const filters: SearchFilters = {
        company_id: companyId || undefined,
        fiscal_year: fiscalYear || undefined,
        fiscal_quarter: fiscalQuarter || undefined,
        calendar_year: calendarYear || undefined,
        calendar_quarter: calendarQuarter || undefined,
        category: category || undefined,
        type: type || undefined,
      };

      const response = await testSearch(
        query,
        parseInt(topK, 10),
        backend,
        backend === SearchBackend.TURBOPUFFER ? namespace : undefined,
        filters,
      );
      setResults(response.results);
      setSearchTime(response.timeTaken);
    } catch (err) {
      setError('An error occurred during the search. Please check the console.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Heading level={1}>Search Test Page</Heading>
        <Text>Test and debug the RAG pipeline by sending queries to the search backend.</Text>
      </div>
      <div className={styles.form}>
        <TextInput
          label="Query"
          value={query}
          onChange={setQuery}
          placeholder="Enter your search query..."
          disabled={isLoading}
        />
        <TextInput
          label="Top K"
          type="number"
          value={topK}
          onChange={setTopK}
          disabled={isLoading}
        />
        <TextInput
          label="Namespace"
          value={namespace}
          onChange={setNamespace}
          disabled={isLoading || backend !== SearchBackend.TURBOPUFFER}
          placeholder="Enter namespace for Turbopuffer"
        />
        <div className={styles.filters}>
          <TextInput
            label="company_id"
            value={companyId}
            onChange={setCompanyId}
            disabled={isLoading}
          />
          <TextInput
            label="fiscal_year"
            value={fiscalYear}
            onChange={setFiscalYear}
            disabled={isLoading}
          />
          <TextInput
            label="fiscal_quarter"
            value={fiscalQuarter}
            onChange={setFiscalQuarter}
            disabled={isLoading}
          />
          <TextInput
            label="calendar_year"
            value={calendarYear}
            onChange={setCalendarYear}
            disabled={isLoading}
          />
          <TextInput
            label="calendar_quarter"
            value={calendarQuarter}
            onChange={setCalendarQuarter}
            disabled={isLoading}
          />
          <TextInput
            label="category"
            value={category}
            onChange={setCategory}
            disabled={isLoading}
          />
          <TextInput label="type" value={type} onChange={setType} disabled={isLoading} />
        </div>
        <div className={styles.checkboxContainer}>
          <label>
            <input
              type="checkbox"
              checked={backend === SearchBackend.TURBOPUFFER}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setBackend(e.target.checked ? SearchBackend.TURBOPUFFER : SearchBackend.SUPABASE)
              }
              disabled={isLoading}
            />
            Use Turbopuffer
          </label>
        </div>
        <Button onClick={handleSubmit} variant="primary" disabled={isLoading}>
          {isLoading ? 'Searching...' : 'Search'}
        </Button>
        {error && <Text color="error">{error}</Text>}
      </div>

      <div className={styles.resultsContainer}>
        {isLoading && <Text>Loading...</Text>}
        {searchTime !== null && (
          <Text size="small" color="neutral-500">
            Search took {(searchTime / 1000).toFixed(2)}s
          </Text>
        )}
        <div className={styles.resultsGrid}>
          {results.map((result) => (
            <ResultCard key={result.id} result={result} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchTestPage;
